import React from 'react'
import PropTypes from 'prop-types'
import styles from './styles.module.css'

/**
 * <Reveal> — the ONE LCP-safe reveal primitive (task #79).
 *
 * Replaces the ad-hoc `opacity: ${p => p.show ? 1 : 0}` + JS (useState /
 * useEffect-on-mount / scroll-listener / IntersectionObserver) reveal pattern
 * that was serving above-the-fold content `opacity:0` and only flipping it to
 * `1` after hydration. On throttled mobile that JS chain took seconds, so the
 * revealed content painted late and became a multi-second-render-delay LCP.
 *
 * Design invariants:
 *
 *  1. ABOVE-THE-FOLD / priority / reduced-motion => render VISIBLE IMMEDIATELY.
 *     No opacity:0 in SSR, no JS gate. This is the default: any content that
 *     *could* be in the first viewport (or is the LCP) must paint on first
 *     render. Pass `priority` (alias `aboveFold`) to be explicit; it is also
 *     the default when `scroll` is not set.
 *
 *  2. BELOW-THE-FOLD => opt in with `scroll`. The fade is CSS-only
 *     (`animation-timeline: view()`), so it never gates paint and needs no JS.
 *     Browsers without scroll-timeline (or with `prefers-reduced-motion:
 *     reduce`) simply render the content visible — it degrades to
 *     no-animation, NEVER to hidden-forever.
 *
 *  3. Server HTML === client first render in every mode (no `typeof window`,
 *     no mount flip), so there is no hydration mismatch (see #20/#23 FOUC).
 *
 * When in doubt, DON'T pass `scroll` — the element renders visible.
 */
function Reveal({ scroll = false, priority = false, aboveFold = false, as, className, children, ...rest }) {
  // Priority / above-the-fold force visible-immediate even if `scroll` was
  // mistakenly passed — the LCP invariant wins over the animation.
  const useScroll = scroll && !priority && !aboveFold

  const Tag = as || 'div'
  const classes = [styles.reveal, useScroll && styles.scroll, className]
    .filter(Boolean)
    .join(' ')

  return (
    <Tag className={classes} {...rest}>
      {children}
    </Tag>
  )
}

Reveal.propTypes = {
  // Opt IN to the below-the-fold CSS scroll-driven fade. Omit for
  // visible-immediate (the LCP-safe default).
  scroll: PropTypes.bool,
  // Force visible-immediate; overrides `scroll`. Use for anything that could be
  // the LCP / in the initial viewport.
  priority: PropTypes.bool,
  aboveFold: PropTypes.bool,
  // Render as a different element/component (styled-components polymorphism).
  as: PropTypes.elementType,
  className: PropTypes.string,
  children: PropTypes.node,
}

export default Reveal
